import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccessControl } from '../App';
import './ServiceCharge.css';

const CHANNELS = ['STOREFRONT', 'WHATSAPP', 'POS', 'MANUAL', 'DIRECT_PAYMENT'];
const defaultPlatformForm = {
  enabled: false,
  enabledMerchantIds: '',
  processorRate: '',
  processorFixed: '',
  processorCap: '',
  processorWaiver: '',
  platformVatEnabled: true,
  platformVatRate: '',
  channelDefaults: {
    STOREFRONT: 'customer',
    WHATSAPP: 'customer',
    POS: 'merchant',
    MANUAL: 'merchant',
    DIRECT_PAYMENT: 'merchant'
  }
};

const rateToPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n <= 1 ? Math.round(n * 100 * 1000) / 1000 : n;
};

const percentToRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n / 100) * 100000) / 100000;
};

const moneyInput = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '';
};

function formFromPlatformSettings(settings) {
  const rules = settings?.rules || {};
  const processor = rules.processorRules?.PAYSTACK_LOCAL || {};
  const platformVat = rules.platformVat || {};
  return {
    enabled: Boolean(settings?.enabled),
    enabledMerchantIds: Array.isArray(settings?.enabledMerchantIds)
      ? settings.enabledMerchantIds.join('\n')
      : '',
    processorRate: moneyInput(rateToPercent(processor.rate ?? 0.015)),
    processorFixed: moneyInput(processor.fixed ?? 100),
    processorCap: moneyInput(processor.cap ?? 2000),
    processorWaiver: moneyInput(processor.fixedFeeWaiverThreshold ?? 2500),
    platformVatEnabled: platformVat.enabled !== false,
    platformVatRate: moneyInput(rateToPercent(platformVat.rate ?? 0.075)),
    channelDefaults: {
      ...defaultPlatformForm.channelDefaults,
      ...(rules.channelDefaults || {})
    }
  };
}

function ServiceCharge() {
  const { apiWithAccessControl } = useAccessControl();
  const navigate = useNavigate();

  // View mode: 'merchant', 'tiers', or 'platform'
  const [viewMode, setViewMode] = useState('merchant');

  // Merchant-specific state
  const [merchantId, setMerchantId] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [customRate, setCustomRate] = useState('');
  const [showCustomRate, setShowCustomRate] = useState(false);

  // Tier settings state
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierEditData, setTierEditData] = useState({
    rate: '',
    whoPays: 'customer',
    isActive: true,
    platformRate: '',
    platformCap: '',
    platformWaiver: ''
  });
  const [platformFeeSettings, setPlatformFeeSettings] = useState(null);
  const [platformForm, setPlatformForm] = useState(defaultPlatformForm);
  const [loadingPlatform, setLoadingPlatform] = useState(false);

  useEffect(() => {
    loadMerchants();
    loadTiers();
    loadPlatformFeeConfig();
  }, []);

  const loadTiers = async () => {
    try {
      setLoadingTiers(true);
      const r = await apiWithAccessControl('/admin/payments/service-charges');
      const d = await r.json();
      if (r.ok) {
        setTiers(d.serviceCharges || []);
        // The dedicated config request owns the rollout form. A slower tier
        // response must not overwrite a newly saved global switch/allowlist.
      }
    } catch (e) {
      console.error('Failed to load tiers:', e);
    } finally {
      setLoadingTiers(false);
    }
  };

  const loadPlatformFeeConfig = async () => {
    try {
      setLoadingPlatform(true);
      const r = await apiWithAccessControl('/admin/payments/platform-fee-config');
      const d = await r.json();
      if (r.ok) {
        setPlatformFeeSettings(d.settings || null);
        setPlatformForm(formFromPlatformSettings(d.settings));
        if (Array.isArray(d.tiers) && d.tiers.length > 0) {
          setTiers(prev => prev.length > 0 ? prev : d.tiers.map(t => ({
            ...t,
            rate: t.rate ?? 0,
            whoPays: t.whoPays || 'customer',
            isActive: t.isActive !== false
          })));
        }
      }
    } catch (e) {
      console.error('Failed to load platform fee config:', e);
    } finally {
      setLoadingPlatform(false);
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier.tier);
    setTierEditData({
      rate: tier.rate.toString(),
      whoPays: tier.whoPays || 'customer',
      isActive: tier.isActive !== false,
      platformRate: moneyInput(tier.platformFee?.rate ?? ''),
      platformCap: moneyInput(tier.platformFee?.cap ?? ''),
      platformWaiver: moneyInput(tier.platformFee?.waiverThreshold ?? '')
    });
    setMsg('');
    setErr('');
  };

  const handleSaveTier = async (tierName) => {
    try {
      setLoading(true);
      setErr('');
      setMsg('');

      const r = await apiWithAccessControl(`/admin/payments/service-charges/${tierName}`, {
        method: 'PUT',
        body: JSON.stringify({
          rate: parseFloat(tierEditData.rate),
          whoPays: tierEditData.whoPays,
          isActive: tierEditData.isActive,
          platformFee: {
            rate: parseFloat(tierEditData.platformRate || 0),
            cap: parseFloat(tierEditData.platformCap || 0),
            waiverThreshold: parseFloat(tierEditData.platformWaiver || 0)
          }
        })
      });

      const d = await r.json();

      if (r.ok) {
        setMsg(`${tierName} tier service charge updated successfully`);
        setEditingTier(null);
        loadTiers(); // Refresh tiers
        loadPlatformFeeConfig();
      } else {
        setErr(d.error || 'Failed to update tier');
      }
    } catch (e) {
      setErr('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingTier(null);
    setTierEditData({
      rate: '',
      whoPays: 'customer',
      isActive: true,
      platformRate: '',
      platformCap: '',
      platformWaiver: ''
    });
  };

  const savePlatformConfig = async () => {
    setErr('');
    setMsg('');
    setLoadingPlatform(true);
    try {
      const merchantIds = platformForm.enabledMerchantIds
        .split(/\s|,|;/)
        .map(id => id.trim())
        .filter(Boolean);

      const r = await apiWithAccessControl('/admin/payments/platform-fee-config', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: platformForm.enabled,
          enabledMerchantIds: merchantIds,
          processorRules: {
            PAYSTACK_LOCAL: {
              rate: percentToRate(platformForm.processorRate),
              fixed: parseFloat(platformForm.processorFixed || 0),
              cap: parseFloat(platformForm.processorCap || 0),
              fixedFeeWaiverThreshold: parseFloat(platformForm.processorWaiver || 0)
            }
          },
          platformVat: {
            enabled: platformForm.platformVatEnabled,
            rate: percentToRate(platformForm.platformVatRate)
          },
          channelDefaults: platformForm.channelDefaults
        })
      });
      const d = await r.json();

      if (r.ok) {
        setPlatformFeeSettings(d.settings);
        setPlatformForm(formFromPlatformSettings(d.settings));
        setMsg('Platform fee v2 configuration updated successfully');
        loadTiers();
      } else {
        setErr(d.error || 'Failed to update platform fee configuration');
      }
    } catch (e) {
      setErr('Network error: ' + e.message);
    } finally {
      setLoadingPlatform(false);
    }
  };

  const loadMerchants = async () => {
    try {
      setLoadingMerchants(true);
      const r = await apiWithAccessControl('/admin/merchants?page=1&limit=1000');
      const d = await r.json();
      if (r.ok) {
        setMerchants(d.merchants || []);
      }
    } catch (e) {
      console.error('Failed to load merchants:', e);
    } finally {
      setLoadingMerchants(false);
    }
  };

  const handleMerchantSelect = (e) => {
    const selectedId = e.target.value;
    setMerchantId(selectedId);
    const merchant = merchants.find(m => m.id === selectedId);
    setSelectedMerchant(merchant);
    setData(null);
    setErr('');
    setMsg('');
    setShowCustomRate(false);
    setCustomRate('');
  };

  const fetchSettings = async () => {
    if (!merchantId) return;
    setErr('');
    setMsg('');
    setData(null);
    setLoading(true);
    try {
      const r = await apiWithAccessControl(`/admin/finance/service-charge/${merchantId}`);
      const d = await r.json();
      if (r.ok) {
        setData(d);
        setCustomRate(d.serviceChargeSettings?.rate || '');
      } else {
        setErr(d.error || 'Error');
      }
    } catch {
      setErr('Network error');
    } finally {
      setLoading(false);
    }
  };

  const save = async (payload) => {
    setErr('');
    setMsg('');
    setLoading(true);
    console.log('Saving service charge settings:', payload);
    try {
      const r = await apiWithAccessControl(`/admin/finance/service-charge/${merchantId}`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      const d = await r.json();
      console.log('Save response:', d);
      if (r.ok) {
        setMsg('Settings saved successfully');
        setData(d);
        setShowCustomRate(false);
      } else {
        setErr(d.error || 'Error saving settings');
      }
    } catch (e) {
      console.error('Save error:', e);
      setErr('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomRateSubmit = (whoPays) => {
    const rate = parseFloat(customRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      setErr('Please enter a valid rate between 0 and 100');
      return;
    }
    save({
      enabled: true,
      whoPays,
      rate: rate.toString()
    });
  };

  useEffect(() => {
    if (merchantId) {
      fetchSettings();
    }
  }, [merchantId]);

  const defaultRate = data?.tierConfig?.rate || '2.5';
  const isEnabled = data?.serviceChargeSettings?.enabled;
  const currentWhoPays = data?.serviceChargeSettings?.whoPays || 'customer';
  const currentRate = data?.serviceChargeSettings?.rate || defaultRate;

  return (
    <div className="service-charge-container">
      <div className="sc-header">
        <div className="sc-header-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 className="sc-title">Service Charge Management</h1>
              <p className="sc-subtitle">
                {viewMode === 'merchant'
                  ? 'Configure merchant-specific service charge settings and overrides'
                  : viewMode === 'tiers'
                    ? 'Manage default service charge rates by tier and the new tier platform fee caps'
                    : 'Control the platform fee v2 rollout, processor recovery, platform VAT, and channel payer defaults'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setViewMode('merchant')}
                style={{
                  padding: '10px 20px',
                  background: viewMode === 'merchant' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  border: `2px solid ${viewMode === 'merchant' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                By Merchant
              </button>
              <button
                onClick={() => setViewMode('tiers')}
                style={{
                  padding: '10px 20px',
                  background: viewMode === 'tiers' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  border: `2px solid ${viewMode === 'tiers' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
                By Tier
              </button>
              <button
                onClick={() => setViewMode('platform')}
                style={{
                  padding: '10px 20px',
                  background: viewMode === 'platform' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                  border: `2px solid ${viewMode === 'platform' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
                  borderRadius: '8px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1v22"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
                Platform Fee v2
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="sc-content">
        {/* Messages */}
        {err && (
          <div className="sc-alert sc-alert-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{err}</span>
          </div>
        )}

        {msg && (
          <div className="sc-alert sc-alert-success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>{msg}</span>
          </div>
        )}

        {/* PLATFORM FEE V2 VIEW */}
        {viewMode === 'platform' && (
          <div className="sc-config-section">
            <div className="sc-card">
              <div className="sc-card-header">
                <div className="sc-card-icon sc-icon-settings">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 1v22"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </div>
                <div>
                  <h2 className="sc-card-title">Platform Fee v2 Rollout</h2>
                  <p className="sc-card-desc">Keep global rollout off while testing individual merchants from the allow-list.</p>
                </div>
              </div>

              {loadingPlatform && !platformFeeSettings ? (
                <div className="sc-loading">
                  <div className="sc-spinner"></div>
                  <p>Loading platform fee configuration...</p>
                </div>
              ) : (
                <div className="sc-platform-panel">
                  <div className="sc-platform-grid">
                    <label className="sc-toggle-row">
                      <input
                        type="checkbox"
                        checked={platformForm.enabled}
                        onChange={(e) => setPlatformForm({ ...platformForm, enabled: e.target.checked })}
                      />
                      <span>
                        <strong>Global v2 enabled</strong>
                        <small>When off, only merchants in the allow-list use v2.</small>
                      </span>
                    </label>

                    <label className="sc-toggle-row">
                      <input
                        type="checkbox"
                        checked={platformForm.platformVatEnabled}
                        onChange={(e) => setPlatformForm({ ...platformForm, platformVatEnabled: e.target.checked })}
                      />
                      <span>
                        <strong>Platform VAT on Revstar fee</strong>
                        <small>Excluded from merchant storefront checkout. Storefront VAT uses the merchant's own tax settings.</small>
                      </span>
                    </label>
                  </div>

                  <div className="sc-form-section">
                    <h3>Merchant allow-list</h3>
                    <textarea
                      className="sc-textarea"
                      value={platformForm.enabledMerchantIds}
                      onChange={(e) => setPlatformForm({ ...platformForm, enabledMerchantIds: e.target.value })}
                      placeholder="Paste merchant user IDs, one per line"
                      rows={5}
                    />
                  </div>

                  <div className="sc-form-section">
                    <h3>Paystack local processor recovery</h3>
                    <div className="sc-field-grid">
                      <label>
                        <span>Rate</span>
                        <input type="number" step="0.01" min="0" max="100" value={platformForm.processorRate} onChange={(e) => setPlatformForm({ ...platformForm, processorRate: e.target.value })} />
                        <small>%</small>
                      </label>
                      <label>
                        <span>Fixed fee</span>
                        <input type="number" step="1" min="0" value={platformForm.processorFixed} onChange={(e) => setPlatformForm({ ...platformForm, processorFixed: e.target.value })} />
                        <small>NGN</small>
                      </label>
                      <label>
                        <span>Cap</span>
                        <input type="number" step="1" min="0" value={platformForm.processorCap} onChange={(e) => setPlatformForm({ ...platformForm, processorCap: e.target.value })} />
                        <small>NGN</small>
                      </label>
                      <label>
                        <span>Fixed-fee waiver below</span>
                        <input type="number" step="1" min="0" value={platformForm.processorWaiver} onChange={(e) => setPlatformForm({ ...platformForm, processorWaiver: e.target.value })} />
                        <small>NGN</small>
                      </label>
                      <label>
                        <span>Platform VAT rate</span>
                        <input type="number" step="0.1" min="0" max="100" value={platformForm.platformVatRate} onChange={(e) => setPlatformForm({ ...platformForm, platformVatRate: e.target.value })} />
                        <small>%</small>
                      </label>
                    </div>
                  </div>

                  <div className="sc-form-section">
                    <h3>Default payer by channel</h3>
                    <div className="sc-channel-grid">
                      {CHANNELS.map(channel => (
                        <label key={channel}>
                          <span>{channel.replace('_', ' ')}</span>
                          <select
                            value={platformForm.channelDefaults[channel] || 'customer'}
                            onChange={(e) => setPlatformForm({
                              ...platformForm,
                              channelDefaults: {
                                ...platformForm.channelDefaults,
                                [channel]: e.target.value
                              }
                            })}
                          >
                            <option value="customer">Customer pays</option>
                            <option value="merchant">Merchant pays</option>
                          </select>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="sc-platform-actions">
                    <button className="sc-btn sc-btn-primary" onClick={savePlatformConfig} disabled={loadingPlatform}>
                      {loadingPlatform ? 'Saving...' : 'Save Platform Fee Config'}
                    </button>
                    <button className="sc-btn sc-btn-secondary" onClick={loadPlatformFeeConfig} disabled={loadingPlatform}>
                      Refresh
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TIER MANAGEMENT VIEW */}
        {viewMode === 'tiers' && (
          <div className="sc-card">
            <div className="sc-card-header">
              <div className="sc-card-icon sc-icon-tier">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              </div>
              <div>
                <h2 className="sc-card-title">Tier Default Service Charges</h2>
                <p className="sc-card-desc">Set default service charge rates for each merchant tier. Merchants without custom settings will use these defaults.</p>
              </div>
            </div>

            {loadingTiers ? (
              <div className="sc-loading">
                <div className="sc-spinner"></div>
                <p>Loading tier settings...</p>
              </div>
            ) : (
              <div style={{ marginTop: '20px' }}>
                {tiers.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    <p>No tier configurations found. Please run the database migration to add service charge fields to the Tier model.</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    {tiers.map(tier => (
                      <div
                        key={tier.tier}
                        style={{
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          padding: '20px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                              <span style={{
                                background: tier.tier === 'FREE' ? '#10b981' :
                                            tier.tier === 'STARTER' ? '#3b82f6' :
                                            tier.tier === 'PRO' ? '#8b5cf6' :
                                            tier.tier === 'PROFESSIONAL' ? '#f59e0b' :
                                            tier.tier === 'ENTERPRISE' ? '#ef4444' : '#6b7280',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '600',
                                textTransform: 'uppercase'
                              }}>
                                {tier.tier}
                              </span>
                              <span style={{
                                background: tier.isActive ? '#d1fae5' : '#fee2e2',
                                color: tier.isActive ? '#065f46' : '#991b1b',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}>
                                {tier.isActive ? 'ENABLED' : 'DISABLED'}
                              </span>
                            </div>
                            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                              {tier.description || `Default service charge for ${tier.name || tier.tier} tier merchants`}
                            </p>
                          </div>

                          {editingTier === tier.tier ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '300px' }}>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '60px' }}>Rate:</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <input
                                    type="number"
                                    value={tierEditData.rate}
                                    onChange={(e) => setTierEditData({...tierEditData, rate: e.target.value})}
                                    style={{
                                      width: '80px',
                                      padding: '8px 12px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '14px'
                                    }}
                                    step="0.1"
                                    min="0"
                                    max="100"
                                  />
                                  <span>%</span>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '60px' }}>Who Pays:</label>
                                <select
                                  value={tierEditData.whoPays}
                                  onChange={(e) => setTierEditData({...tierEditData, whoPays: e.target.value})}
                                  style={{
                                    padding: '8px 12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    flex: 1
                                  }}
                                >
                                  <option value="customer">Customer</option>
                                  <option value="merchant">Merchant</option>
                                </select>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <label style={{ fontSize: '14px', fontWeight: '500', minWidth: '60px' }}>Status:</label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={tierEditData.isActive}
                                    onChange={(e) => setTierEditData({...tierEditData, isActive: e.target.checked})}
                                    style={{ width: '18px', height: '18px' }}
                                  />
                                  <span style={{ fontSize: '14px' }}>Enabled</span>
                                </label>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '500' }}>
                                  Platform rate %
                                  <input
                                    type="number"
                                    value={tierEditData.platformRate}
                                    onChange={(e) => setTierEditData({...tierEditData, platformRate: e.target.value})}
                                    style={{ width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                                    step="0.1"
                                    min="0"
                                    max="100"
                                  />
                                </label>
                                <label style={{ fontSize: '13px', fontWeight: '500' }}>
                                  Cap NGN
                                  <input
                                    type="number"
                                    value={tierEditData.platformCap}
                                    onChange={(e) => setTierEditData({...tierEditData, platformCap: e.target.value})}
                                    style={{ width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                                    step="1"
                                    min="0"
                                  />
                                </label>
                                <label style={{ fontSize: '13px', fontWeight: '500' }}>
                                  Waiver below NGN
                                  <input
                                    type="number"
                                    value={tierEditData.platformWaiver}
                                    onChange={(e) => setTierEditData({...tierEditData, platformWaiver: e.target.value})}
                                    style={{ width: '100%', marginTop: '4px', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                                    step="1"
                                    min="0"
                                  />
                                </label>
                              </div>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button
                                  onClick={() => handleSaveTier(tier.tier)}
                                  disabled={loading}
                                  style={{
                                    padding: '8px 16px',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    opacity: loading ? 0.7 : 1
                                  }}
                                >
                                  {loading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={loading}
                                  style={{
                                    padding: '8px 16px',
                                    background: '#f3f4f6',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500'
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{tier.rate}%</div>
                                <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'capitalize' }}>
                                  {tier.whoPays === 'merchant' ? 'Merchant pays' : 'Customer pays'}
                                </div>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(90px, 1fr))', gap: '10px', minWidth: '300px' }}>
                                <div className="sc-mini-stat">
                                  <span>V2 rate</span>
                                  <strong>{tier.platformFee?.rate ?? 0}%</strong>
                                </div>
                                <div className="sc-mini-stat">
                                  <span>V2 cap</span>
                                  <strong>₦{Number(tier.platformFee?.cap || 0).toLocaleString()}</strong>
                                </div>
                                <div className="sc-mini-stat">
                                  <span>Waiver</span>
                                  <strong>₦{Number(tier.platformFee?.waiverThreshold || 0).toLocaleString()}</strong>
                                </div>
                              </div>
                              <button
                                onClick={() => handleEditTier(tier)}
                                style={{
                                  padding: '8px 16px',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M12 20h9"/>
                                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                                </svg>
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="sc-info-note" style={{ marginTop: '20px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <div>
                    <strong>How Tier Defaults Work:</strong>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                      <li>These are the <strong>default</strong> service charge rates for each tier</li>
                      <li>When a merchant doesn't have custom settings, they use their tier's default rate</li>
                      <li>Merchant-specific overrides (set in "By Merchant" view) take priority</li>
                      <li>Changes here affect all merchants in the tier who don't have custom settings</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* MERCHANT MANAGEMENT VIEW */}
        {viewMode === 'merchant' && (
          <>
        {/* Merchant Selection Card */}
        <div className="sc-card sc-merchant-selector">
          <div className="sc-card-header">
            <div className="sc-card-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <h2 className="sc-card-title">Select Merchant</h2>
              <p className="sc-card-desc">Choose a merchant to manage their service charge settings</p>
            </div>
          </div>

          {loadingMerchants ? (
            <div className="sc-loading">
              <div className="sc-spinner"></div>
              <p>Loading merchants...</p>
            </div>
          ) : (
            <div className="sc-select-wrapper">
              <select
                className="sc-select"
                value={merchantId}
                onChange={handleMerchantSelect}
              >
                <option value="">-- Select a merchant --</option>
                {merchants.map(merchant => (
                  <option key={merchant.id} value={merchant.id}>
                    {merchant.username || merchant.email}
                    {merchant.store?.name ? ` - ${merchant.store.name}` : ''}
                    {' '}({merchant.merchantTier || 'FREE'})
                  </option>
                ))}
              </select>
              <div className="sc-select-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>
          )}

          {selectedMerchant && (
            <div className="sc-merchant-info">
              <div className="sc-info-grid">
                <div className="sc-info-item">
                  <span className="sc-info-label">Email</span>
                  <span className="sc-info-value">{selectedMerchant.email}</span>
                </div>
                <div className="sc-info-item">
                  <span className="sc-info-label">Username</span>
                  <span className="sc-info-value">{selectedMerchant.username || 'N/A'}</span>
                </div>
                <div className="sc-info-item">
                  <span className="sc-info-label">Store</span>
                  <span className="sc-info-value">{selectedMerchant.store?.name || 'N/A'}</span>
                </div>
                <div className="sc-info-item">
                  <span className="sc-info-label">Tier</span>
                  <span className="sc-tier-badge">
                    {selectedMerchant.merchantTier || 'FREE'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        {err && (
          <div className="sc-alert sc-alert-error">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{err}</span>
          </div>
        )}

        {msg && (
          <div className="sc-alert sc-alert-success">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>{msg}</span>
          </div>
        )}

        {loading && !data && (
          <div className="sc-loading">
            <div className="sc-spinner"></div>
            <p>Loading settings...</p>
          </div>
        )}

        {/* Configuration Cards */}
        {data && (
          <div className="sc-config-section">
            {/* Tier Default Configuration */}
            {data.tierConfig && (
              <div className="sc-card sc-tier-config">
                <div className="sc-card-header">
                  <div className="sc-card-icon sc-icon-tier">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div>
                    <h2 className="sc-card-title">Tier Default Configuration</h2>
                    <p className="sc-card-desc">{data.merchantTier} tier defaults</p>
                  </div>
                </div>

                <div className="sc-tier-details">
                  <div className="sc-tier-item">
                    <span className="sc-tier-label">Default Rate</span>
                    <span className="sc-tier-value">{data.tierConfig.rate}%</span>
                  </div>
                  <div className="sc-tier-item">
                    <span className="sc-tier-label">Default Who Pays</span>
                    <span className="sc-tier-value sc-capitalize">{data.tierConfig.whoPays}</span>
                  </div>
                  <div className="sc-tier-item sc-full-width">
                    <span className="sc-tier-label">Description</span>
                    <span className="sc-tier-desc">{data.tierConfig.description}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Current Settings Overview */}
            <div className="sc-card sc-current-settings">
              <div className="sc-card-header">
                <div className="sc-card-icon sc-icon-settings">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"/>
                  </svg>
                </div>
                <div>
                  <h2 className="sc-card-title">Current Settings</h2>
                  <p className="sc-card-desc">Merchant-specific overrides</p>
                </div>
              </div>

              <div className="sc-settings-grid">
                <div className="sc-setting-card">
                  <div className="sc-setting-header">
                    <span className="sc-setting-label">Status</span>
                  </div>
                  <div className={`sc-status-badge ${isEnabled ? 'sc-status-enabled' : 'sc-status-disabled'}`}>
                    <span className="sc-status-dot"></span>
                    {isEnabled ? 'ENABLED' : 'DISABLED'}
                  </div>
                </div>

                <div className="sc-setting-card">
                  <div className="sc-setting-header">
                    <span className="sc-setting-label">Who Pays</span>
                  </div>
                  <div className="sc-setting-value sc-capitalize">{currentWhoPays}</div>
                </div>

                <div className="sc-setting-card">
                  <div className="sc-setting-header">
                    <span className="sc-setting-label">Rate</span>
                  </div>
                  <div className="sc-setting-value">{currentRate}%</div>
                </div>
              </div>
            </div>

            {/* Actions Card */}
            <div className="sc-card sc-actions-card">
              <div className="sc-card-header">
                <div className="sc-card-icon sc-icon-actions">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="sc-card-title">Quick Actions</h2>
                  <p className="sc-card-desc">Apply predefined service charge settings</p>
                </div>
              </div>

              <div className="sc-actions-grid">
                <button
                  className="sc-action-btn sc-btn-primary"
                  onClick={() => save({
                    enabled: true,
                    whoPays: 'customer',
                    rate: defaultRate
                  })}
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <div>
                    <div className="sc-btn-title">Enable (Customer Pays)</div>
                    <div className="sc-btn-subtitle">Use tier default: {defaultRate}%</div>
                  </div>
                </button>

                <button
                  className="sc-action-btn sc-btn-secondary"
                  onClick={() => save({
                    enabled: true,
                    whoPays: 'merchant',
                    rate: defaultRate
                  })}
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 7h-3a2 2 0 0 1-2-2V2"/>
                    <path d="M9 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7l4 4v10a2 2 0 0 1-2 2Z"/>
                    <path d="M3 7.6v12.8A1.6 1.6 0 0 0 4.6 22h9.8"/>
                  </svg>
                  <div>
                    <div className="sc-btn-title">Enable (Merchant Pays)</div>
                    <div className="sc-btn-subtitle">Use tier default: {defaultRate}%</div>
                  </div>
                </button>

                <button
                  className="sc-action-btn sc-btn-custom"
                  onClick={() => setShowCustomRate(!showCustomRate)}
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                  </svg>
                  <div>
                    <div className="sc-btn-title">Set Custom Rate</div>
                    <div className="sc-btn-subtitle">Override tier default</div>
                  </div>
                </button>

                <button
                  className="sc-action-btn sc-btn-danger"
                  onClick={() => save({ enabled: false })}
                  disabled={loading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  <div>
                    <div className="sc-btn-title">Disable Service Charge</div>
                    <div className="sc-btn-subtitle">Turn off for this merchant</div>
                  </div>
                </button>
              </div>

              {/* Custom Rate Panel */}
              {showCustomRate && (
                <div className="sc-custom-rate-panel">
                  <div className="sc-custom-rate-header">
                    <h3>Set Custom Rate</h3>
                    <p>Enter a custom service charge percentage (0-100)</p>
                  </div>
                  <div className="sc-custom-rate-content">
                    <div className="sc-input-group">
                      <input
                        type="number"
                        className="sc-input"
                        placeholder="Enter rate"
                        value={customRate}
                        onChange={(e) => setCustomRate(e.target.value)}
                        min="0"
                        max="100"
                        step="0.1"
                      />
                      <span className="sc-input-suffix">%</span>
                    </div>
                    <div className="sc-custom-rate-actions">
                      <button
                        className="sc-btn sc-btn-sm sc-btn-primary"
                        onClick={() => handleCustomRateSubmit('customer')}
                        disabled={loading || !customRate}
                      >
                        Apply (Customer Pays)
                      </button>
                      <button
                        className="sc-btn sc-btn-sm sc-btn-secondary"
                        onClick={() => handleCustomRateSubmit('merchant')}
                        disabled={loading || !customRate}
                      >
                        Apply (Merchant Pays)
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="sc-info-note">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <div>
                  <strong>How Service Charges Work:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Merchant-specific settings override tier defaults</li>
                    <li>Service charges are automatically calculated during checkout</li>
                    <li>Tier defaults: FREE=4.5%, STARTER=3.5%, PRO=3%, PROFESSIONAL=2.5%, ENTERPRISE=2%</li>
                    <li>Use "Set Custom Rate" to override the tier default for this merchant</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default ServiceCharge;
